import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { avatarUrl as av } from '../utils/imageUrl';
import LocationBanner from '../components/LocationBanner';
import { FaUsers, FaCheckCircle, FaCalendarCheck, FaTicketAlt, FaStar, FaMoneyBillWave, FaDownload, FaLock } from 'react-icons/fa';

import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import '../styles/AnalyticsPage.css';

const AnalyticsPage = () => {
  const { business } = useAuth();
  const { locationId } = useLocation();
  const currentTier = business?.premium_tier || 1;
  const [period, setPeriod] = useState('month'); // week, month, year, all
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCheckIns: 0,
    totalBookings: 0,
    totalCouponsUsed: 0,
    averageRating: 0,
    estimatedRevenue: 0
  });
  const [chartData, setChartData] = useState({
    daily: [],
    couponTypes: [],
    hourlyDistribution: [],
    bookingHourly: [],
    bookingWeekday: [],
  });
  const [monthComparison, setMonthComparison] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageTab, setPageTab] = useState('main'); // 'main' | 'daily'
  const [dailyReport, setDailyReport] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);

  useEffect(() => {
    loadAnalytics();
    loadMonthComparison();
  }, [business, period, locationId]);

  useEffect(() => {
    if (pageTab === 'daily') loadDailyReport();
  }, [pageTab, business?.id, locationId]);

  const getDateRange = () => {
    const now = new Date();
    let startDate = new Date();

    switch (period) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'all':
        startDate = new Date('2024-01-01');
        break;
      default:
        startDate.setMonth(now.getMonth() - 1);
    }

    return { startDate: startDate.toISOString(), endDate: now.toISOString() };
  };

  const TIER_POINTS = { '5-20': 10, '20-50': 35, '50+': 80 };
  const TIER_MIN_AZN = { '5-20': 5, '20-50': 20, '50+': 50 };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRange();

      // 1. QR Scan logs (point_logs) — real ziyarət datası
      const { data: scanLogs } = await supabase
        .from('point_logs')
        .select('*')
        .eq('business_id', business.id)
        .gte('created_at', startDate)
        .lte('created_at', endDate)
        .order('created_at', { ascending: true });

      const scans = scanLogs || [];

      // 2. Bookings (accepted)
      const { data: bookings } = await supabase
        .from('flash_bookings')
        .select('*')
        .eq('business_id', business.id)
        .eq('status', 'accepted')
        .gte('created_at', startDate)
        .lte('created_at', endDate);

      // 3. Coupon logs
      const { data: allCouponLogs } = await supabase
        .rpc('get_coupon_logs', { p_business_id: business.id });
      const couponsUsed = (allCouponLogs || []).filter(c =>
        c.created_at >= startDate && c.created_at <= endDate
      );

      // 4. Unique users (scan-lardan)
      const uniqueUsers = new Set(scans.map(s => s.user_id).filter(Boolean));

      // 5. Top users (ən çox scan edilən)
      const userScanCount = {};
      scans.forEach(s => {
        if (!s.user_id) return;
        if (!userScanCount[s.user_id]) {
          userScanCount[s.user_id] = { user_id: s.user_id, full_name: s.user_name || 'İstifadəçi', count: 0 };
        }
        userScanCount[s.user_id].count++;
      });
      const topUsersList = Object.values(userScanCount)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // 6. Günlük scan qrafiki
      const dailyData = {};
      scans.forEach(s => {
        const date = new Date(s.created_at).toLocaleDateString('az-AZ');
        dailyData[date] = (dailyData[date] || 0) + 1;
      });
      const dailyChartData = Object.entries(dailyData).map(([date, count]) => ({ date, count }));

      // 7. Kupon növləri qrafiki
      const couponTypeCount = {};
      couponsUsed.forEach(c => {
        const type = c.coupon_title || 'Digər';
        couponTypeCount[type] = (couponTypeCount[type] || 0) + 1;
      });
      const couponTypesData = Object.entries(couponTypeCount).map(([name, value]) => ({ name, value }));

      // 8. Saatlara görə ziyarət
      const hourlyData = {};
      scans.forEach(s => {
        const hour = new Date(s.created_at).getHours();
        hourlyData[hour] = (hourlyData[hour] || 0) + 1;
      });
      const hourlyChartData = Object.entries(hourlyData)
        .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
        .map(([hour, count]) => ({ hour: `${hour}:00`, count }));

      // 9. Real rating
      const { data: reviewsData } = await supabase
        .from('reviews').select('rating').eq('business_id', business.id);
      const avgRating = reviewsData?.length
        ? (reviewsData.reduce((s, r) => s + r.rating, 0) / reviewsData.length).toFixed(1)
        : 0;

      // 10. Təxmini gəlir — scan tier-lərindən min ödəniş
      const estimatedRevenue = scans.reduce((sum, s) => {
        return sum + (TIER_MIN_AZN[s.amount_tier] || 0);
      }, 0);

      setStats({
        totalUsers: uniqueUsers.size,
        totalCheckIns: scans.length,
        totalBookings: bookings?.length || 0,
        totalCouponsUsed: couponsUsed.length,
        averageRating: avgRating,
        estimatedRevenue,
      });

      // 11. Rezervasiya saatlıq analizi
      const bookingHourly = {};
      (bookings || []).forEach(b => {
        if (!b.reservation_time) return;
        const hour = parseInt(b.reservation_time.slice(0, 2));
        bookingHourly[hour] = (bookingHourly[hour] || 0) + 1;
      });
      const bookingHourlyData = Array.from({ length: 24 }, (_, h) => ({
        hour: `${h}:00`, count: bookingHourly[h] || 0,
      })).filter(x => x.count > 0);

      // 12. Həftə günləri analizi (scan-lardan)
      const WEEKDAYS_AZ = ['Baz', 'Baz.e', 'Çər.a', 'Çər', 'Cüm.a', 'Cüm', 'Şən'];
      const weekdayData = {};
      scans.forEach(s => {
        const day = new Date(s.created_at).getDay();
        weekdayData[day] = (weekdayData[day] || 0) + 1;
      });
      const bookingWeekdayData = Array.from({ length: 7 }, (_, d) => ({
        day: WEEKDAYS_AZ[d], count: weekdayData[d] || 0,
      }));

      setChartData({
        daily: dailyChartData,
        couponTypes: couponTypesData,
        hourlyDistribution: hourlyChartData,
        bookingHourly: bookingHourlyData,
        bookingWeekday: bookingWeekdayData,
      });

      setTopUsers(topUsersList);

    } catch (error) {
      console.error('Analytics error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthComparison = async () => {
    if (!business?.id) return;
    try {
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const thisMonthEnd   = now.toISOString();
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
      const lastMonthEnd   = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59).toISOString();

      const [thisScans, lastScans, thisBk, lastBk] = await Promise.all([
        supabase.from('point_logs').select('id', { count: 'exact' })
          .eq('business_id', business.id).gte('created_at', thisMonthStart).lte('created_at', thisMonthEnd),
        supabase.from('point_logs').select('id', { count: 'exact' })
          .eq('business_id', business.id).gte('created_at', lastMonthStart).lte('created_at', lastMonthEnd),
        supabase.from('flash_bookings').select('id', { count: 'exact' })
          .eq('business_id', business.id).eq('status', 'accepted').gte('created_at', thisMonthStart).lte('created_at', thisMonthEnd),
        supabase.from('flash_bookings').select('id', { count: 'exact' })
          .eq('business_id', business.id).eq('status', 'accepted').gte('created_at', lastMonthStart).lte('created_at', lastMonthEnd),
      ]);

      const diff = (cur, prev) => {
        if (!prev) return null;
        const pct = prev === 0 ? (cur > 0 ? 100 : 0) : Math.round(((cur - prev) / prev) * 100);
        return { current: cur, previous: prev, pct, up: pct >= 0 };
      };

      setMonthComparison({
        scans:    diff(thisScans.data?.length || 0, lastScans.data?.length || 0),
        bookings: diff(thisBk.data?.length || 0,   lastBk.data?.length || 0),
        month:    now.toLocaleString('az-AZ', { month: 'long' }),
        prevMonth: new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleString('az-AZ', { month: 'long' }),
      });
    } catch (e) { console.error('monthComparison:', e); }
  };

  const loadDailyReport = async () => {
    if (!business?.id) return;
    setDailyLoading(true);
    try {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const todayEnd   = now.toISOString();
      const yestStart  = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).toISOString();
      const yestEnd    = todayStart;

      const [todayScansRes, yestScansRes, todayBkRes, yestBkRes, todayRevRes, yestRevRes] = await Promise.all([
        supabase.from('point_logs').select('*').eq('business_id', business.id).gte('created_at', todayStart).lte('created_at', todayEnd),
        supabase.from('point_logs').select('id', { count: 'exact', head: true }).eq('business_id', business.id).gte('created_at', yestStart).lte('created_at', yestEnd),
        supabase.from('flash_bookings').select('*').eq('business_id', business.id).gte('created_at', todayStart).lte('created_at', todayEnd),
        supabase.from('flash_bookings').select('id', { count: 'exact', head: true }).eq('business_id', business.id).eq('status', 'accepted').gte('created_at', yestStart).lte('created_at', yestEnd),
        supabase.from('reviews').select('id, rating, comment, created_at').eq('business_id', business.id).gte('created_at', todayStart).lte('created_at', todayEnd),
        supabase.from('reviews').select('id', { count: 'exact', head: true }).eq('business_id', business.id).gte('created_at', yestStart).lte('created_at', yestEnd),
      ]);

      const todayScans = todayScansRes.data || [];
      const todayBk    = todayBkRes.data || [];
      const todayRevs  = todayRevRes.data || [];

      // Hourly check-in distribution
      const hourMap = {};
      todayScans.forEach(s => {
        const h = new Date(s.created_at).getHours();
        hourMap[h] = (hourMap[h] || 0) + 1;
      });
      const hourlyChart = Object.entries(hourMap)
        .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
        .map(([h, count]) => ({ hour: `${h}:00`, count }));

      const peakHour = hourlyChart.length > 0
        ? [...hourlyChart].sort((a, b) => b.count - a.count)[0]
        : null;

      const acceptedBk = todayBk.filter(b => b.status === 'accepted');
      const pendingBk  = todayBk.filter(b => b.status === 'pending');

      const dateLabel = now.toLocaleDateString('az-AZ', { weekday: 'long', day: 'numeric', month: 'long' });

      setDailyReport({
        dateLabel,
        checkIns:  { today: todayScans.length,    yesterday: yestScansRes.count || 0 },
        bookings:  { today: acceptedBk.length,    yesterday: yestBkRes.count   || 0, pending: pendingBk.length },
        reviews:   { today: todayRevs.length,     yesterday: yestRevRes.count  || 0, list: todayRevs },
        hourlyChart,
        peakHour,
        recentScans: [...todayScans].reverse().slice(0, 6),
      });
    } catch (e) {
      console.error('Daily report error:', e);
    } finally {
      setDailyLoading(false);
    }
  };

  const exportCSV = () => {
    const rows = [
      ['Göstərici', 'Dəyər'],
      ['Unikal İstifadəçi', stats.totalUsers],
      ['Check-in Sayı', stats.totalCheckIns],
      ['Qəbul Edilən Rezervasiya', stats.totalBookings],
      ['İstifadə Edilən Kupon', stats.totalCouponsUsed],
      ['Orta Reytinq', stats.averageRating],
      ['Təxmini Gəlir (₼)', stats.estimatedRevenue],
      [],
      ['Tarix', 'Check-in'],
      ...chartData.daily.map(d => [d.date, d.count]),
      [],
      ['Saat', 'Ziyarət'],
      ...chartData.hourlyDistribution.map(d => [d.hour, d.count]),
      [],
      ['Ən Aktiv Müştərilər', 'Check-in Sayı'],
      ...topUsers.map(u => [u.full_name, u.count]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hesabat_${period}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  const getCouponTitle = (type) => {
    const titles = {
      'free_latte': 'Pulsuz Latte',
      'free_burger': 'Pulsuz Burger',
      'free_dessert': 'Pulsuz Desert',
      'free_drink': 'Pulsuz İçki',
      'discount_10': '10% Endirim',
      'discount_20': '20% Endirim'
    };
    return titles[type] || type;
  };

  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a'];

  if (loading) {
    return (
      <div className="analytics-loading">
        <h2>Yüklənir...</h2>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <LocationBanner />
      <div className="analytics-header">
        <div>
          <h1>📊 Hesabatlar və Audit</h1>
          <p>Cafegoo proqramının kafenizə gətirdiyi fayda</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            {[{ key: 'main', label: 'Ümumi' }, { key: 'daily', label: '📅 Günlük Hesabat' }].map(t => (
              <button
                key={t.key}
                onClick={() => setPageTab(t.key)}
                style={{
                  padding: '7px 18px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                  background: pageTab === t.key ? '#E63950' : '#f0f0f0',
                  color: pageTab === t.key ? '#fff' : '#555',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {currentTier >= 3 ? (
            <button
              onClick={exportCSV}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: '#8B5CF6', color: '#fff', fontWeight: 600,
                fontSize: 13, cursor: 'pointer',
              }}
            >
              <FaDownload style={{ fontSize: 12 }} />
              CSV Export
            </button>
          ) : (
            <div
              title="Premium plana keçin"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8,
                background: '#f5f5f5', color: '#aaa', fontWeight: 600,
                fontSize: 13, cursor: 'not-allowed', border: '1px solid #e0e0e0',
              }}
            >
              <FaLock style={{ fontSize: 11 }} />
              CSV Export
            </div>
          )}
        </div>
        {pageTab === 'main' && (
          <div className="period-selector">
            <button className={period === 'week'  ? 'active' : ''} onClick={() => setPeriod('week')}>Son Həftə</button>
            <button className={period === 'month' ? 'active' : ''} onClick={() => setPeriod('month')}>Son Ay</button>
            <button className={period === 'year'  ? 'active' : ''} onClick={() => setPeriod('year')}>Son İl</button>
            <button className={period === 'all'   ? 'active' : ''} onClick={() => setPeriod('all')}>Bütün Vaxt</button>
          </div>
        )}
      </div>

      {/* ── GÜNLÜK HESABAT TAB ── */}
      {pageTab === 'daily' && (
        <div>
          {dailyLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 15 }}>Yüklənir...</div>
          ) : dailyReport ? (
            <>
              {/* Date */}
              <div style={{ marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1a1a2e', textTransform: 'capitalize' }}>
                  {dailyReport.dateLabel}
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>
                  Dünənlə müqayisəli günlük statistika
                </p>
              </div>

              {/* Stat Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
                {[
                  { label: 'QR Check-in', today: dailyReport.checkIns.today, yesterday: dailyReport.checkIns.yesterday, icon: '📲', color: '#667eea' },
                  { label: 'Qəbul edilən rezervasiya', today: dailyReport.bookings.today, yesterday: dailyReport.bookings.yesterday, icon: '✅', color: '#22c55e' },
                  { label: 'Gözləyən rezervasiya', today: dailyReport.bookings.pending, yesterday: null, icon: '⏳', color: '#f59e0b' },
                  { label: 'Yeni rəy', today: dailyReport.reviews.today, yesterday: dailyReport.reviews.yesterday, icon: '⭐', color: '#E63950' },
                ].map(({ label, today, yesterday, icon, color }) => {
                  const diff = yesterday !== null && yesterday > 0
                    ? Math.round(((today - yesterday) / yesterday) * 100) : null;
                  const up = diff !== null && diff >= 0;
                  return (
                    <div key={label} style={{
                      background: '#fff', borderRadius: 14, padding: '18px 20px',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      borderLeft: `4px solid ${color}`,
                    }}>
                      <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e' }}>{today}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
                      {diff !== null && (
                        <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: up ? '#16a34a' : '#dc2626' }}>
                          {up ? '▲' : '▼'} {Math.abs(diff)}%
                          <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4 }}>
                            dünən: {yesterday}
                          </span>
                        </div>
                      )}
                      {diff === null && yesterday === null && today === 0 && (
                        <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>Gözləyən yoxdur</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Hourly chart */}
              {dailyReport.hourlyChart.length > 0 ? (
                <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
                    🕐 Bugünkü Saatlara Görə Check-in
                    {dailyReport.peakHour && (
                      <span style={{ marginLeft: 10, fontSize: 12, color: '#667eea', fontWeight: 600 }}>
                        · Ən sıx: {dailyReport.peakHour.hour} ({dailyReport.peakHour.count} scan)
                      </span>
                    )}
                  </h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={dailyReport.hourlyChart}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" fontSize={11} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#667eea" name="Check-in" radius={[5, 5, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ background: '#f8fafc', borderRadius: 14, padding: '30px', textAlign: 'center', color: '#94a3b8', marginBottom: 20, fontSize: 14 }}>
                  📲 Bu gün hələ check-in yoxdur
                </div>
              )}

              {/* Recent scans */}
              {dailyReport.recentScans.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 20 }}>
                  <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
                    Son Check-inlər
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {dailyReport.recentScans.map((s, i) => (
                      <div key={s.id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f0f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#667eea', fontWeight: 700, flexShrink: 0 }}>
                          {(s.user_name || 'M')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{s.user_name || 'İstifadəçi'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{TIER_MIN_AZN[s.amount_tier] ? `${TIER_MIN_AZN[s.amount_tier]}₼+` : ''} {s.amount_tier || ''}</div>
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          {new Date(s.created_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Today's reviews */}
              {dailyReport.reviews.list.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 14, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
                    ⭐ Bugünkü Rəylər
                  </h3>
                  {dailyReport.reviews.list.map((r, i) => (
                    <div key={r.id || i} style={{ padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <div style={{ fontSize: 14 }}>{'⭐'.repeat(r.rating || 0)}</div>
                      {r.comment && <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{r.comment}</div>}
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                        {new Date(r.created_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* ── ÜMUMI TAB CONTENT ── */}
      {pageTab === 'main' && <>

      {/* ── AYLIK MÜQAYİSƏ ── */}
      {monthComparison && (
        <div style={{
          background: '#fff', borderRadius: 16, padding: '20px 24px', marginBottom: 24,
          boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Aylıq Müqayisə — {monthComparison.prevMonth} → {monthComparison.month}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            {[
              { label: 'QR Scan', data: monthComparison.scans, icon: '📲' },
              { label: 'Rezervasiya', data: monthComparison.bookings, icon: '📅' },
            ].map(({ label, data, icon }) => data && (
              <div key={label} style={{
                background: data.up ? '#f0fdf4' : '#fef2f2',
                borderRadius: 12, padding: '14px 18px',
                border: `1.5px solid ${data.up ? '#bbf7d0' : '#fecaca'}`,
              }}>
                <div style={{ fontSize: 20, marginBottom: 8 }}>{icon}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#1a1a2e' }}>{data.current}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
                <div style={{
                  marginTop: 6, fontSize: 13, fontWeight: 700,
                  color: data.up ? '#16a34a' : '#dc2626',
                }}>
                  {data.up ? '▲' : '▼'} {Math.abs(data.pct)}%
                  <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11, marginLeft: 4 }}>
                    keçən ay: {data.previous}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="analytics-stats-grid">
        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#667eea' }}>
            <FaUsers />
          </div>
          <div className="stat-content">
            <h3>{stats.totalUsers}</h3>
            <p>Unikal İstifadəçi</p>
            <span className="stat-description">Proqramdan gələn müştərilər</span>
          </div>
        </div>

        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#27ae60' }}>
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.totalCheckIns}</h3>
            <p>Check-in Sayı</p>
            <span className="stat-description">Aktiv ziyarətlər</span>
          </div>
        </div>

        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#f39c12' }}>
            <FaCalendarCheck />
          </div>
          <div className="stat-content">
            <h3>{stats.totalBookings}</h3>
            <p>Qəbul Edilən Rezervasiya</p>
            <span className="stat-description">Flash booking-lər</span>
          </div>
        </div>

        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#e74c3c' }}>
            <FaTicketAlt />
          </div>
          <div className="stat-content">
            <h3>{stats.totalCouponsUsed}</h3>
            <p>İstifadə Edilən Kupon</p>
            <span className="stat-description">Kampanya təsiri</span>
          </div>
        </div>

        <div className="analytics-stat-card">
          <div className="stat-icon" style={{ color: '#feca57' }}>
            <FaStar />
          </div>
          <div className="stat-content">
            <h3>{stats.averageRating}</h3>
            <p>Orta Reytinq</p>
            <span className="stat-description">Müştəri məmnuniyyəti</span>
          </div>
        </div>

        <div className="analytics-stat-card highlight">
          <div className="stat-icon" style={{ color: '#2ecc71' }}>
            <FaMoneyBillWave />
          </div>
          <div className="stat-content">
            <h3>{stats.estimatedRevenue}₼+</h3>
            <p>Proqramdan Gəlir</p>
            <span className="stat-description">Scan tierlərindən min. həcm</span>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        {/* Daily Check-ins */}
        <div className="chart-card full-width">
          <h3>📈 Günlük Check-in Statistikası</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData.daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#667eea" strokeWidth={2} name="Check-in" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Hourly Distribution */}
        <div className="chart-card">
          <h3>🕐 Saatlara Görə Ziyarət</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData.hourlyDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#764ba2" name="Ziyarət" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Booking Hourly */}
        {chartData.bookingHourly.length > 0 && (
          <div className="chart-card">
            <h3>📅 Rezervasiya Saatları</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.bookingHourly}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#E63950" name="Rezervasiya" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Weekday Distribution */}
        <div className="chart-card">
          <h3>📆 Həftə Günləri Analizi</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData.bookingWeekday}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#667eea" name="Ziyarət" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Coupon Types */}
        <div className="chart-card">
          <h3>🎟️ Kupon Növləri</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.couponTypes}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${entry.value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.couponTypes.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Users */}
      <div className="top-users-card">
        <h3>🏆 Ən Aktiv Müştərilər (Top 10)</h3>
        {topUsers.length === 0 ? (
          <p className="empty-state">Hələ məlumat yoxdur</p>
        ) : (
          <div className="top-users-list">
            {topUsers.map((user, index) => (
              <div key={user.user_id} className="top-user-item">
                <div className="user-rank">{index + 1}</div>
                {user.avatar_url ? (
                  <img src={av(user.avatar_url)} alt={user.full_name} className="user-avatar-small" />
                ) : (
                  <div className="user-avatar-placeholder-small">{user.full_name[0]}</div>
                )}
                <div className="user-name">{user.full_name}</div>
                <div className="user-count">{user.count} check-in</div>
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Insights */}
      <div className="insights-card">
        <h3>💡 Analiz və Tövsiyələr</h3>
        <div className="insights-list">
          <div className="insight-item success">
            <span className="insight-icon">✅</span>
            <p>
              Proqramdan <strong>{stats.totalUsers} unikal müştəri</strong> gəlib,{' '}
              cəmi <strong>{stats.totalCheckIns} scan</strong> edilib.
            </p>
          </div>

          {chartData.hourlyDistribution.length > 0 && (() => {
            const peak = [...chartData.hourlyDistribution].sort((a, b) => b.count - a.count)[0];
            return (
              <div className="insight-item info">
                <span className="insight-icon">📊</span>
                <p>
                  Ən sıx saat: <strong>{peak.hour}</strong> — bu saatda <strong>{peak.count} scan</strong> edilib.
                </p>
              </div>
            );
          })()}

          {stats.estimatedRevenue > 0 && (
            <div className="insight-item warning">
              <span className="insight-icon">💰</span>
              <p>
                Proqram vasitəsilə ən azı <strong>{stats.estimatedRevenue}₼</strong> dövriyyə yaranıb (scan tierlərindən hesablanıb).
              </p>
            </div>
          )}

          {stats.totalCouponsUsed > 0 && (
            <div className="insight-item success">
              <span className="insight-icon">🎟️</span>
              <p>
                Bu dövrdə <strong>{stats.totalCouponsUsed} kupon</strong> kafedə oxudulub.
                {chartData.couponTypes[0] && <> Ən populyar: <strong>{chartData.couponTypes.sort((a,b)=>b.value-a.value)[0]?.name}</strong>.</>}
              </p>
            </div>
          )}

          {stats.totalBookings > 0 && (
            <div className="insight-item info">
              <span className="insight-icon">📅</span>
              <p>
                <strong>{stats.totalBookings} rezervasiya</strong> qəbul edilib.
              </p>
            </div>
          )}

          {stats.averageRating > 0 && (
            <div className="insight-item">
              <span className="insight-icon">⭐</span>
              <p>
                Ortalama reytinq: <strong>{stats.averageRating}/5</strong>.{' '}
                {stats.averageRating >= 4.5
                  ? 'Əla nəticə — müştərilər çox razıdır!'
                  : stats.averageRating >= 4
                  ? 'Yaxşı nəticə — bir az daha yaxşılaşdırmaq mümkündür.'
                  : 'Müştəri şərhlərini izləyin və xidməti yaxşılaşdırın.'}
              </p>
            </div>
          )}
        </div>
      </div>

      </>}
    </div>
  );
};

export default AnalyticsPage;